import { createStore } from '@/shared/stores/config';

import type { IRainSendAttachment } from '../hooks/types';

/**
 * A draft's first message, handed off from NewDraftChat to the editor purely
 * for instant optimistic display. The actual send already happened at the
 * source (NewDraftChat), so this carries no send semantics.
 */
export interface IRainPendingMessage {
  content: string;
  attachments: IRainSendAttachment[];
}

/**
 * In-memory hand-off for a draft's first Rain message.
 *
 * NewDraftChat sends the first message to the freshly-created thread itself
 * (send-at-source) and then navigates instantly. To render that message
 * optimistically the moment the editor opens — without re-sending it — it
 * stashes the message here, keyed by thread id, and the editor consumes it
 * exactly once on mount.
 *
 * Deliberately NOT persisted: a reload finds it empty and falls back to the
 * server backlog, and consume-once means a remount finds it already taken.
 * Either way the message renders once and the send is never re-triggered.
 */
export interface IRainPendingFirstMessageState {
  pendingByThreadId: Record<string, IRainPendingMessage>;

  /** Stash the first message for a thread (overwrites any prior entry). */
  setPendingRainMessage: (
    threadId: string,
    message: IRainPendingMessage,
  ) => void;

  /**
   * Return and remove the pending message for a thread. Returns `undefined`
   * when there is none (already consumed, or this isn't a fresh draft).
   */
  consumePendingRainMessage: (
    threadId: string,
  ) => IRainPendingMessage | undefined;
}

export const useRainPendingFirstMessageStore =
  createStore<IRainPendingFirstMessageState>(
    'rain-pending-first-message-store',
    (_, get, api) => ({
      pendingByThreadId: {},

      setPendingRainMessage: (threadId, message) => {
        api.setState(
          state => ({
            pendingByThreadId: {
              ...state.pendingByThreadId,
              [threadId]: message,
            },
          }),
          false,
          { type: 'rainPendingFirstMessage/set', threadId },
        );
      },

      consumePendingRainMessage: threadId => {
        const pending = get().pendingByThreadId[threadId];
        if (!pending) return undefined;

        api.setState(
          state => {
            const { [threadId]: _removed, ...rest } = state.pendingByThreadId;
            return { pendingByThreadId: rest };
          },
          false,
          { type: 'rainPendingFirstMessage/consume', threadId },
        );
        return pending;
      },
    }),
  );

export const selectSetPendingRainMessage = (
  state: IRainPendingFirstMessageState,
) => state.setPendingRainMessage;

export const selectConsumePendingRainMessage = (
  state: IRainPendingFirstMessageState,
) => state.consumePendingRainMessage;
